/** @format */

import {alpha, styled, type Theme} from '@mui/material/styles';
import {Box} from '@mui/material';

import {type StatusVariantType} from 'pages/HomeGrouping/types';

// Systemic badge text — the «p14» group: 14px / fixed 16px line-height / medium weight.
const BADGE_TEXT = {
  fontSize: 14,
  lineHeight: '16px',
  fontWeight: 500,
} as const;

// Systemic badge base — every status-row badge is built from this: 8px/16px padding so the capsule
// is 32px tall (16px content line + 8+8), p14 text, single line. Radius/colors are added per badge.
const badgeBase = (theme: Theme) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
  whiteSpace: 'nowrap' as const,
  userSelect: 'none' as const,
  ...BADGE_TEXT,
});

// Background / text colors for a project-status badge, mapped from its variant onto the
// semantic_dark palette (same tokens the sibling critical / no-mediaplan badges use).
const statusColors = (variant: StatusVariantType, theme: Theme) => {
  switch (variant) {
    case 'error':
      return {
        backgroundColor: theme.palette.semantic_dark?.bad.container,
        color: theme.palette.semantic_dark?.bad.onContainer,
      };
    case 'warning':
      return {
        backgroundColor: theme.palette.semantic_dark?.alert.container,
        color: theme.palette.semantic_dark?.alert.onContainer,
      };
    default:
      return {
        backgroundColor: alpha(theme.palette.semantic_dark?.surface.onSurface ?? '#fafbfe', 0.1),
        color: theme.palette.semantic_dark?.surface.onSurface,
      };
  }
};

// Row layout: flex-wrap + gap 8px (from Figma node 10282:122471 — gap-[8px], flex-wrap)
const StatusRowRoot = styled(Box)(({theme}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
}));

// Project status badge (technical problems) — overview-native so it matches the sibling badges
// systemically (the HomeGrouping ProjectStatus uses the legacy palette / 28px height and is shared
// with other pages). Capsule with a trailing help icon that opens the explanation popover.
const StatusBadgeRoot = styled(Box, {
  shouldForwardProp: prop => prop !== 'colorscheme',
})<{colorscheme: StatusVariantType}>(({theme, colorscheme}) => ({
  ...badgeBase(theme),
  gap: theme.spacing(1),
  borderRadius: 16,
  cursor: 'pointer',
  ...statusColors(colorscheme, theme),
  // Trailing help icon sized to the text line so the badge stays 32px tall.
  '& .material-symbols-outlined': {
    fontSize: 16,
    lineHeight: '16px',
  },
}));

// Segmented pill joining the sensors + critical badges (Figma node 10284:126945 / 10282:122481):
// one rounded container (radius 16) with overflow clip and a 1px gap, so the two badges read as
// halves of a single pill (the gap shows the card surface). A lone sensors badge keeps the radius.
// When the pill no longer fits the screen (≤520px) the badges stack vertically, full width
// (Figma node 10286:129294) — the 1px gap turns into a horizontal divider, corners stay rounded.
const SensorPill = styled(Box)(() => ({
  display: 'inline-flex',
  alignItems: 'stretch',
  gap: '1px',
  borderRadius: 16,
  overflow: 'hidden',
  width: 'fit-content',
  maxWidth: '100%',
  '@media (max-width:520px)': {
    flexDirection: 'column',
    width: '100%',
  },
}));

// Triggered sensors badge — left half of SensorPill. Leading `sensors` Material Symbol (16px).
// Figma node 10284:126946: bg = rgba(250,251,254,0.1); text = --surface/on-surface (#fafbfe).
// Padding: px-16px py-8px; font 14/16 medium. No own radius — clipped by SensorPill.
const CounterChip = styled(Box)(({theme}) => ({
  ...badgeBase(theme),
  gap: theme.spacing(1),
  backgroundColor: alpha(theme.palette.semantic_dark?.surface.onSurface ?? '#fafbfe', 0.1),
  color: theme.palette.semantic_dark?.surface.onSurface,
}));

// Critical badge — right half of SensorPill (shown only when critical > 0).
// Figma node 10284:126947: bg = --bad/bad-container (#6f3a3a); text = --bad/on-bad-container (#fefafa).
// Padding: px-16px py-8px; font 14/16 medium. No own radius — clipped by SensorPill.
const CriticalChip = styled(Box)(({theme}) => ({
  ...badgeBase(theme),
  backgroundColor: theme.palette.semantic_dark?.bad.container,
  color: theme.palette.semantic_dark?.bad.onContainer,
}));

// No-mediaplan chip (shown when hasMediaplan === false)
// Styling matches CriticalChip pattern: uses alert.container/onContainer semantic tokens
// Same radius/padding/font as sibling chips (16px radius, 8px 16px padding, 14/16 w500)
const NoMediaplanChip = styled(Box)(({theme}) => ({
  ...badgeBase(theme),
  borderRadius: 16,
  backgroundColor: theme.palette.semantic_dark?.alert.container,
  color: theme.palette.semantic_dark?.alert.onContainer,
}));

export {StatusRowRoot, SensorPill, CounterChip, CriticalChip, NoMediaplanChip, StatusBadgeRoot};
